import React, { useEffect, useMemo, useState } from 'react';

import { Box, DialogContent, DialogTitle, Typography, DialogActions } from '@mui/material';
import Colors from '../../../constants/Colors';
import { ContainedButton, LoadingButton, TextButton } from '../../common/StyledButtons';
import StyledDialog from '../../common/StyledDialog';
import { StyledTextField } from '../../common/StyledTextField';
import { StyledDatePicker } from '../../common/StyledDatePicker';
import dayjs from 'dayjs';
import { cancelClockifyTimerForAdmin, toggleClockifyTimerForAdmin } from '../../../actions/internal/AdminActions';
import { getUserId } from '../../../selectors/UserSelectors';
import { NOTIFICATION_TYPE, showNotificationBar } from '../../../actions/error-pages/NotificationBarActions';
import StyledSwitch from '../../common/StyledSwitch';
import { updateSiteSubmission } from '../../../actions/internal/SiteActions';
import { SUBMISSION_STATUS } from '../../../constants/Statuses';

const TIMER_WORK_SUMMARY_MAX_LENGTH = 200;

const AddTimeEntryModal = ({ open, onClose, onSave, onDiscard, timer, request }) => {
  const [timerWorkSummary, setTimerWorkSummary] = useState('');
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [timerStopTime, setTimerStopTime] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [notifyClient, setNotifyClient] = useState(true);

  useEffect(() => {
    if (!timer) {
      setTimerStartTime(null);
      setTimerStopTime(null);
      return;
    }
    setTimerStartTime(timer.startTime ? dayjs(timer.startTime) : null);
    setTimerStopTime(timer.stopTime ? dayjs(timer.stopTime) : null);
  }, [timer]);

  const submissionStatus =
    request &&
    request.submission_status;
  const isSubmittedRequest = submissionStatus === 'SUBMITTED';

  const timerWorkSummaryCharacterCount = timerWorkSummary.length;
  const isTimerWorkSummaryOverLimit =
    timerWorkSummaryCharacterCount > TIMER_WORK_SUMMARY_MAX_LENGTH;

  const timerDurationLabel = useMemo(() => {
    if (!timerStartTime || !timerStopTime) return 'N/A';
  
    const startTimeValue = timerStartTime.valueOf();
    const stopTimeValue = timerStopTime.valueOf();
  
    if (Number.isNaN(startTimeValue) || Number.isNaN(stopTimeValue)) {
      return 'Invalid time entry';
    }
  
    const diffInSeconds = Math.max(
      0,
      Math.floor((stopTimeValue - startTimeValue) / 1000)
    );
  
    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;
  
    if (
      Number.isNaN(diffInSeconds) ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      Number.isNaN(seconds)
    ) {
      return 'Invalid time entry';
    }
  
    return [hours, minutes, seconds]
      .map((value) => String(value).padStart(2, '0'))
      .join(':');
  }, [timerStartTime, timerStopTime]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmitTimerSummary = async () => {
    if (!timerStartTime || !timerStopTime || !timerWorkSummary) return;

    const startTimeValue = timerStartTime.valueOf();
    const stopTimeValue = timerStopTime.valueOf();
    const submissionId = timer && timer.clockifyTimer && timer.clockifyTimer.submission_id

    if (Number.isNaN(startTimeValue) || Number.isNaN(stopTimeValue)) {
      return;
    }

    try {
      setIsSaving(true);

      const newTimer = await toggleClockifyTimerForAdmin({
        user_id: getUserId(),
        submission_id: submissionId,
        description: timerWorkSummary,
        start: timerStartTime.toISOString(),
        end: timerStopTime.toISOString()
      });

      if (isSubmittedRequest) {
        const site_id = request.submission_identifiers.site ? request.submission_identifiers.site.site_id : undefined;
        const options = { send_submission_notification: notifyClient };
        const masks = ['submission_status']
        const payload = {
          submission_status: SUBMISSION_STATUS.IN_PROGRESS
        }
        await updateSiteSubmission(getUserId(), site_id, submissionId, masks, payload, options)
      }
  
      setTimerWorkSummary('');
      onSave(newTimer);
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Time entry saved successfully.');
    } catch (ex) {
      const err = await ex;
      console.error(err);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Time entry was not able to be saved. Please refresh and try again.');
    } finally {
      setIsSaving(false);
    }


  };

  const handleDiscardTimerSummary = async () => {
    try {
      setIsDiscarding(true);
      const submissionId = timer && timer.clockifyTimer && timer.clockifyTimer.submission_id
      await cancelClockifyTimerForAdmin(getUserId(), submissionId);
      setTimerWorkSummary('');
      onDiscard();
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Time entry discarded successfully.');
    } catch (ex) {
      const err = await ex;
      console.error(err);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Time entry was not able to be discarded. Please refresh and try again.');
    } finally {
      setIsDiscarding(false);
    }

  };

  const handleCancelTimerSummary = async () => {
      setTimerWorkSummary('');
      handleClose();
  };

  const hasInvalidTimeRange =
    timerStartTime &&
    timerStopTime &&
    timerStopTime.valueOf() <= timerStartTime.valueOf();


    const handleRenderStatusUpdateSection = () => (
      <Box
        sx={{
          border: '1px solid',
          borderColor: Colors.primaryColor,
          borderRadius: 2,
          bgcolor: 'background.default',
          px: 2,
          py: 1.5,
          mb: 2.5,
          mt: 2.5
        }}
      >
      <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0, fontWeight: 700, }}
        >
          This request will move to In Progress
        </Typography>

        <p style={{ mb: 1, marginTop: 0, paddingTop: 0 }}>
        Saving this time entry will automatically update the request status.
        </p>
    
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            pt: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
    
          <StyledSwitch 
            title="Notify Client"
            checked={notifyClient}
            onChange={(e) => setNotifyClient(e.target.checked) }
          />
        </Box>
      </Box>
    );

    

  return (
    <StyledDialog
      open={open}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <h4 style={{ margin: 0, padding: 0 }}>What did you work on?</h4>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Start Time
              </Typography>

              <StyledDatePicker
                value={timerStartTime}
                views={['hours', 'minutes']}
                onChange={(date) => {
                  if (!date) return;
                  setTimerStartTime(date);
                }}
                showTimeSelect
                timeIntervals={15}
                dateFormat="MMM d, yyyy h:mm aa"
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Stop Time
              </Typography>

              <StyledDatePicker
                value={timerStopTime}
                views={['hours', 'minutes']}
                onChange={(date) => {
                  if (!date) return;
                  setTimerStopTime(date);
                }}
                showTimeSelect
                timeIntervals={15}
                dateFormat="MMM d, yyyy h:mm aa"
              />
            </Box>
          </Box>

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 1.5,
              bgcolor: 'background.default',
            }}
          >
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Total Duration
              </Typography>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 20,
                lineHeight: 1.3,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {timerDurationLabel}
            </Typography>
          </Box>
        </Box>

        {hasInvalidTimeRange && (
          <Typography
            sx={{
              color: 'error.main',
              fontSize: 13,
              fontWeight: 600,
              mb: 2,
            }}
          >
            Stop time must be after the start time.
          </Typography>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0 }}
        >
          Details
        </Typography>

        <p style={{ mb: 1, marginTop: 0, paddingTop: 0 }}>
          Add a quick note to let the client know what was completed during this time.
        </p>

        <StyledTextField
          fullWidth
          multiline
          minRows={4}
          value={timerWorkSummary}
          onChange={(event) => setTimerWorkSummary(event.target.value)}
          placeholder="Example: Updated homepage content, reviewed client notes, and adjusted mobile styling."
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mt: 0.75,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isTimerWorkSummaryOverLimit ? 'error.main' : 'text.secondary',
              fontWeight: isTimerWorkSummaryOverLimit ? 700 : 400,
            }}
          >
            {timerWorkSummaryCharacterCount}/{TIMER_WORK_SUMMARY_MAX_LENGTH}
          </Typography>
        </Box>
        {isSubmittedRequest && handleRenderStatusUpdateSection()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <TextButton onClick={handleDiscardTimerSummary} sx={{ color: Colors.redColor }} disabled={isSaving || isDiscarding}>
          {isDiscarding ? 'Discarding...' : 'Discard Time'}
        </TextButton>

        <Box>
          <TextButton onClick={handleCancelTimerSummary} disabled={isSaving || isDiscarding}>
            Cancel
          </TextButton>
          <LoadingButton
            onClick={handleSubmitTimerSummary}
            sx={{ marginLeft: '5px'}}
            isLoading={isSaving}
            disabled={
              !timerWorkSummary.trim() ||
              isTimerWorkSummaryOverLimit ||
              !timerStartTime ||
              !timerStopTime ||
              hasInvalidTimeRange ||
              isDiscarding
            }
          >
            Save Time
          </LoadingButton>
        </Box>
        
      </DialogActions>
    </StyledDialog>
  );
};

export default AddTimeEntryModal;